import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AddressGeoApi from 'components/AddressGeoApi/AddressGeoApi';
import Button from 'components/button';
import ModalBody from 'components/modal/components/body';
import ModalFooter from 'components/modal/components/footer';
import JustificatifConfirmation, {
  CONFIRMATION_TITLE,
} from 'components/justificatifConfirmation/JustificatifConfirmation';
import useJustificatifConfirmation from 'hooks/useJustificatifConfirmation';
import Modalheader from 'components/modal/components/header';
import useModal from 'components/modal/useModal';
import StyledInput from 'components/styledInput/StyledInput';
import { buildCompanySupplierFormOptions } from 'constants/missionSupplierFilters';
import {
  diffCompanyOverview,
  isCompanyOverviewValid,
  validateCompanyOverview,
} from 'helper/companyOverviewDiff';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { agreementsOf, draftFromCompany, statusOptionsOf } from 'components/companyForm/companyLegalForm';

const ModalCompanyLegalInformation = () => {
  const { toggle } = useModal();
  const { modalData, setModalDataReturn } = UseContext();
  const companyInfo = modalData?.companyInfo;
  const [draft, setDraft] = useState(() => draftFromCompany(companyInfo));
  const [supplierList, setSupplierList] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const justificatif = useJustificatifConfirmation();
  const token = localStorage.getItem('xsrfToken');

  useEffect(() => {
    setDraft(draftFromCompany(companyInfo));
  }, [companyInfo]);

  useEffect(() => {
    ApiDatabase.getCompanySuppliers(
      {},
      (data) => setSupplierList(Array.isArray(data) ? data : []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  useEffect(() => {
    ApiDatabase.getCollectiveAgreement(
      { token },
      (data) => setAgreements(agreementsOf(data, companyInfo)),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [token, companyInfo?.conv_collective]);

  const editField = (field) => (event) =>
    setDraft((previous) => ({ ...previous, [field]: event.target.value }));

  const selectAgreement = (updated) => {
    // StyledInput hands back its own shape — { value, label, checked } — and
    // storing that as the agreement list left the next render mapping
    // undefined ids and names, so the list came back empty. Only the ticked
    // state is taken from it; the entries keep theirs.
    const selected = updated.find((agreement) => agreement.checked);
    setAgreements((previous) =>
      previous.map((agreement) => ({
        ...agreement,
        checked: agreement.id === selected?.value,
      })),
    );
    setDraft((previous) => ({
      ...previous,
      conv_collective: selected ? selected.value : null,
    }));
  };

  const statusOptions = statusOptionsOf(companyInfo);

  const errors = validateCompanyOverview(draft);
  const modifiedFields = diffCompanyOverview(draft, companyInfo);
  const canConfirm =
    isCompanyOverviewValid(errors) && Object.keys(modifiedFields).length > 0;

  // What the admin is confirming, frozen on the way into the step.
  //
  // The form is unmounted while the document is chosen, so these values cannot
  // change — but `modifiedFields` is recomputed on every render from `draft`
  // against `companyInfo`, and an effect rebuilds `draft` whenever
  // `companyInfo` changes identity. Left live, that comparison can collapse to
  // "nothing modified" underneath an open confirmation and grey out its
  // button, and a save that did go through would send values nobody reviewed.
  const [pendingFields, setPendingFields] = useState(null);

  const submit = () => {
    // These fields identify the company on every contract it signs, so the
    // change carries the client's written request for it.
    if (!justificatif.isConfirming) {
      if (!canConfirm) return;
      setPendingFields(modifiedFields);
      return justificatif.setIsConfirming(true);
    }
    justificatif.confirm(save);
  };

  const save = (storedJustificatif) => {
    setIsLoading(true);
    ApiDatabase.postUpdateCompanyOverview(
      {
        token,
        idCompany: companyInfo?._id,
        companyInfo: pendingFields ?? modifiedFields,
        justificatif: storedJustificatif,
      },
      () => {
        setIsLoading(false);
        toast.success(
          "Les informations de l'entreprise ont été mises à jour avec succès.",
        );
        // The tab renders labels, not ids: the patch is resolved here, where
        // the reference lists are already loaded, rather than in the tab.
        const resolved = { ...modifiedFields };
        if (modifiedFields.conv_collective) {
          const agreement = agreements.find(
            (item) =>
              item.id?.toString() ===
              modifiedFields.conv_collective?.toString(),
          );
          if (agreement) resolved.conv_collective = agreement.name;
        }
        if ('companySupplier' in modifiedFields) {
          resolved.companySupplier = modifiedFields.companySupplier
            ? supplierList.find(
                (supplier) =>
                  supplier._id?.toString() ===
                  modifiedFields.companySupplier?.toString(),
              ) || null
            : null;
        }
        setModalDataReturn({
          type: 'company_legal_information_updated',
          companyInfo: resolved,
        });
        toggle();
      },
      (err) => {
        setIsLoading(false);
        console.error(err);
        toast.error(
          'Une erreur est survenue lors de la mise à jour des informations.',
        );
      },
    );
  };

  const selectedAgreementLabel =
    agreements.find((agreement) => agreement.checked)?.name || '-';

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {justificatif.isConfirming ? CONFIRMATION_TITLE : 'Informations légales'}
      </Modalheader>
      <ModalBody>
        {justificatif.isConfirming ? (
          <JustificatifConfirmation
            file={justificatif.file}
            setFile={justificatif.setFile}
            isAttested={justificatif.isAttested}
            setIsAttested={justificatif.setIsAttested}
            error={justificatif.error}
            description={
              "Ces informations identifient l'entreprise sur ses contrats. Un justificatif est requis avant validation."
            }
          />
        ) : (
          <>
        {/* The modal structure sets no height of its own, so the body only
            scrolls once something bounds it. Bounded here rather than in the
            structure, which every modal of the admin shares. The dropdowns are
            unaffected: Popper positions them against the viewport. */}
        <div className={'flex flex-col gap-4 max-h-[60vh] overflow-y-auto'}>
          <label className={'flex flex-col gap-1'}>
            Raison sociale
            <StyledInput
              type={'text'}
              value={draft.reason_social}
              onChange={editField('reason_social')}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Nom commercial
            <StyledInput
              type={'text'}
              value={draft.name_commercial}
              onChange={editField('name_commercial')}
            />
          </label>
          <div className={'flex flex-col gap-1'}>
            Adresse
            <AddressGeoApi
              addressGeoValue={(address) =>
                setDraft((previous) => ({ ...previous, address }))
              }
              addressValue={draft.address}
              page={'editCompany'}
            />
          </div>
          <label className={'flex flex-col gap-1'}>
            SIRET
            <StyledInput
              type={'number'}
              value={draft.siret}
              onChange={editField('siret')}
              error={errors.errorSiret}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Forme juridique
            <StyledInput
              type={'select'}
              value={draft.status}
              list={statusOptions}
              onChange={editField('status')}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Fournisseur
            <StyledInput
              type={'select'}
              value={draft.companySupplier || ''}
              list={buildCompanySupplierFormOptions(supplierList)}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  companySupplier: event.target.value || null,
                }))
              }
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Code APE
            <StyledInput
              type={'text'}
              value={draft.code_ape}
              onChange={editField('code_ape')}
              error={errors.errorCodeApe}
            />
          </label>
          <label className={'flex flex-col gap-1'}>
            Effectif
            <StyledInput
              type={'number'}
              value={draft.staff}
              onChange={editField('staff')}
              error={errors.errorStaff}
            />
          </label>
          <div className={'flex flex-col gap-1'}>
            Convention collective
            <StyledInput
              type={'selectMultipleRadio'}
              value={selectedAgreementLabel}
              list={agreements.map((agreement) => ({
                value: agreement.id,
                label: agreement.name,
                checked: agreement.checked,
              }))}
              onChange={selectAgreement}
              isSearchable
              dropdownFullWidth
            />
          </div>
        </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          size={'LG'}
          color={'SECONDARY'}
          // Backs out of the confirmation, not the modal: the values being
          // confirmed are still worth seeing.
          onClick={
            justificatif.isConfirming
              ? () => {
                  setPendingFields(null);
                  justificatif.setIsConfirming(false);
                }
              : toggle
          }
        >
          Annuler
        </Button>
        {isLoading || justificatif.isUploading ? (
          <Button size={'LG'} color={'DISABLED'}>
            Confirmation ...
          </Button>
        ) : (
          <Button
            size={'LG'}
            color={
              (
                justificatif.isConfirming
                  ? justificatif.canConfirm
                  : canConfirm
              )
                ? 'PRIMARY'
                : 'DISABLED'
            }
            onClick={submit}
          >
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalCompanyLegalInformation;
